import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
} from '@mui/material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { getChangeOrders } from '../services/api';

const ChangeOrderItem = ({ changeOrder, onClick }) => {
  const statusColors = {
    pending: 'warning',
    approved: 'success',
    rejected: 'error',
    consultation: 'info',
  };

  return (
    <>
      <ListItem button onClick={onClick} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body1" fontWeight="medium">
                {changeOrder.description}
              </Typography>
              <Chip
                label={changeOrder.status}
                size="small"
                color={statusColors[changeOrder.status]}
                sx={{ textTransform: 'capitalize' }}
              />
            </Box>
          }
          secondary={
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {changeOrder.project_name} • {changeOrder.client_name}
              </Typography>
              <Typography variant="caption" display="block">
                Cost Impact: ${changeOrder.cost_impact?.toLocaleString() || 0}
              </Typography>
              {changeOrder.schedule_impact && (
                <Typography variant="caption" display="block">
                  Schedule Impact: {changeOrder.schedule_impact}
                </Typography>
              )}
              <Typography variant="caption" display="block">
                Created by: {changeOrder.created_by_name} •{' '}
                {format(new Date(changeOrder.created_at), 'MMM d, yyyy')}
              </Typography>
            </Box>
          }
        />
      </ListItem>
      <Divider component="li" />
    </>
  );
};

const ChangeOrders = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [changeOrders, setChangeOrders] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadChangeOrders();
  }, []);

  const loadChangeOrders = async () => {
    setLoading(true);
    try {
      const response = await getChangeOrders();
      setChangeOrders(response.data);
    } catch (error) {
      console.error('Failed to load change orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const pendingCOs = changeOrders.filter((co) => co.status === 'pending' || co.status === 'consultation');
  const approvedCOs = changeOrders.filter((co) => co.status === 'approved');
  const rejectedCOs = changeOrders.filter((co) => co.status === 'rejected');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Change Orders
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Track and manage project change orders
      </Typography>

      <Paper elevation={2}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label={`Pending (${pendingCOs.length})`} />
          <Tab label={`Approved (${approvedCOs.length})`} />
          <Tab label={`Rejected (${rejectedCOs.length})`} />
        </Tabs>

        <Box sx={{ p: 2 }}>
          {activeTab === 0 && (
            <List>
              {pendingCOs.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No pending change orders</Typography>
                </Box>
              ) : (
                pendingCOs.map((co) => (
                  <ChangeOrderItem
                    key={co.id}
                    changeOrder={co}
                    onClick={() => navigate(`/projects/${co.project_id}`)}
                  />
                ))
              )}
            </List>
          )}

          {activeTab === 1 && (
            <List>
              {approvedCOs.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No approved change orders</Typography>
                </Box>
              ) : (
                approvedCOs.map((co) => (
                  <ChangeOrderItem
                    key={co.id}
                    changeOrder={co}
                    onClick={() => navigate(`/projects/${co.project_id}`)}
                  />
                ))
              )}
            </List>
          )}

          {activeTab === 2 && (
            <List>
              {rejectedCOs.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No rejected change orders</Typography>
                </Box>
              ) : (
                rejectedCOs.map((co) => (
                  <ChangeOrderItem
                    key={co.id}
                    changeOrder={co}
                    onClick={() => navigate(`/projects/${co.project_id}`)}
                  />
                ))
              )}
            </List>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ChangeOrders;
